import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { API_URL } from '../utils/Functions';

const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    review: '',
    rating: 5,
    avatar: '',
    is_active: true
  });

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/testimonials`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      const res = await response.json();
      if (!response.ok) throw res;
      setTestimonials(res.data || []);
    } catch (err) {
      setError('Failed to load testimonials.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const handleOpenCreate = () => {
    setEditingItem(null);
    setImageFile(null);
    setImagePreview(null);
    setFormData({
      name: '',
      role: '',
      review: '',
      rating: 5,
      avatar: '',
      is_active: true
    });
    setShowFormModal(true);
  };

  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setImageFile(null);
    setImagePreview(item.avatar ? (item.avatar.startsWith('http') ? item.avatar : `${API_URL}${item.avatar.startsWith('/') ? '' : '/'}${item.avatar}`) : null);
    setFormData({
      name: item.name,
      role: item.role,
      review: item.review,
      rating: item.rating,
      avatar: item.avatar || '',
      is_active: !!item.is_active
    });
    setShowFormModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('auth_token');
      const method = 'POST';
      const url = editingItem 
        ? `${API_URL}/api/admin/testimonials/${editingItem.id}`
        : `${API_URL}/api/admin/testimonials`;

      const payload = new FormData();
      payload.append('name', formData.name);
      payload.append('role', formData.role);
      payload.append('review', formData.review);
      payload.append('rating', formData.rating);
      payload.append('is_active', formData.is_active ? 1 : 0);
      if (imageFile) {
        payload.append('avatar', imageFile);
      } else if (formData.avatar && !editingItem) {
        payload.append('avatar', formData.avatar);
      }
      if (editingItem) {
        payload.append('_method', 'PUT');
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: payload
      });

      const res = await response.json();
      if (!response.ok) throw res;

      toast.success(editingItem ? 'Testimonial updated!' : 'Testimonial created!');
      fetchTestimonials();
      setShowFormModal(false);
    } catch (err) {
      toast.error(err.message || 'Failed to save testimonial.');
    }
  };

  const executeDelete = async () => {
    if (!itemToDelete) return;
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/testimonials/${itemToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error('Failed to delete');
      setTestimonials(testimonials.filter(t => t.id !== itemToDelete.id));
      setItemToDelete(null);
      toast.success('Testimonial deleted.');
    } catch (err) {
      toast.error('Error deleting testimonial.');
    }
  };

  const filteredTestimonials = testimonials.filter(t => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (t.name && t.name.toLowerCase().includes(term)) ||
      (t.role && t.role.toLowerCase().includes(term)) ||
      (t.review && t.review.toLowerCase().includes(term))
    );
  });

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        .ts-wrap { font-family: 'Inter', sans-serif; }

        /* Hero Banner */
        .ts-hero {
          background: linear-gradient(135deg, #040f1f 0%, #061830 40%, #CFA047 100%);
          border-radius: 18px; padding: 32px 36px; margin-bottom: 28px;
          display: flex; align-items: center; justify-content: space-between;
          position: relative; overflow: hidden;
        }
        .ts-hero::before { content: ''; position: absolute; top: -40px; right: -40px; width: 240px; height: 240px; border-radius: 50%; background: rgba(255,255,255,0.05); }
        .ts-hero::after { content: ''; position: absolute; bottom: -60px; right: 120px; width: 180px; height: 180px; border-radius: 50%; background: rgba(255,255,255,0.04); }
        .ts-hero-left { position: relative; z-index: 1; }
        .ts-hero-eyebrow { font-size: 11px; font-weight: 700; color: #CFA047; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 10px; display: flex; align-items: center; gap: 6px; }
        .ts-hero-title { font-size: 26px; font-weight: 800; color: #ffffff; margin: 0 0 8px; line-height: 1.2; }
        .ts-hero-sub { font-size: 14px; color: rgba(255,255,255,0.8); margin: 0; }
        .ts-hero-right { position: relative; z-index: 1; display: flex; align-items: center; gap: 16px; }
        .ts-hero-stat { background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.15); border-radius: 14px; padding: 16px 22px; text-align: center; backdrop-filter: blur(6px); }
        .ts-hero-stat-val { font-size: 28px; font-weight: 800; color: #ffffff; line-height: 1; }
        .ts-hero-stat-label { font-size: 11px; color: #CFA047; font-weight: 600; margin-top: 4px; }
        .ts-create-btn { display: flex; align-items: center; gap: 8px; padding: 11px 22px; border-radius: 10px; border: none; background: #ffffff; color: #061830; font-size: 14px; font-weight: 700; cursor: pointer; font-family: 'Inter', sans-serif; box-shadow: 0 4px 14px rgba(0,0,0,0.15); transition: all 0.2s; white-space: nowrap; }
        .ts-create-btn:hover { box-shadow: 0 6px 18px rgba(207, 160, 71,0.4); transform: translateY(-1px); }

        /* Error */
        .ts-error { background: #fff1f2; border: 1px solid #fecdd3; border-radius: 10px; padding: 12px 16px; color: #e11d48; font-size: 13px; margin-bottom: 20px; display: flex; align-items: center; gap: 10px; }

        /* Section Header */
        .ts-section-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; flex-wrap: wrap; gap: 12px; }
        .ts-section-label { font-size: 13px; font-weight: 700; color: #5a6a7e; display: flex; align-items: center; gap: 8px; flex: 1; min-width: 200px; }
        .ts-section-label::after { content: ''; flex: 1; height: 1px; background: #eef1f5; }
        .ts-count-badge { background: #f0f4f8; color: #5a6a7e; font-size: 12px; font-weight: 600; padding: 3px 10px; border-radius: 20px; margin-left: 8px; }
        .ts-search-box { position: relative; width: 280px; }
        .ts-search-box i { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #9babbf; font-size: 13px; }
        .ts-search-box input { width: 100%; padding: 10px 14px 10px 38px; border-radius: 10px; border: 1.5px solid #dde3ea; font-size: 13px; font-family: 'Inter', sans-serif; color: #2d3a4b; outline: none; transition: all 0.2s; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.02); }
        .ts-search-box input:focus { border-color: #CFA047; box-shadow: 0 0 0 3px rgba(207, 160, 71,0.12); }

        /* Grid Layout for Cards */
        .ts-grid-list { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 20px; padding-bottom: 24px; }
        .ts-test-card { background: #fff; border-radius: 14px; border: 1px solid #dde3ea; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.05); transition: all 0.22s; display: flex; flex-direction: column; }
        .ts-test-card:hover { box-shadow: 0 8px 24px rgba(0,0,0,0.1); transform: translateY(-3px); border-color: #CFA047; }
        .ts-card-header { padding: 16px; display: flex; align-items: center; gap: 14px; border-bottom: 1px solid #f1f5f9; }
        .ts-card-avatar { width: 48px; height: 48px; border-radius: 50%; object-fit: cover; background: #f0f4f8; border: 1px solid #dde3ea; flex-shrink: 0; }
        .ts-card-avatar-placeholder { width: 48px; height: 48px; border-radius: 50%; background: #f0f4f8; display: flex; align-items: center; justify-content: center; color: #cbd5e1; font-size: 20px; border: 1px solid #dde3ea; flex-shrink: 0; }
        .ts-card-client-info { flex: 1; overflow: hidden; }
        .ts-card-name { font-weight: 700; color: #1a2332; font-size: 15px; margin-bottom: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ts-card-role { font-size: 12px; color: #8496a9; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ts-card-body { padding: 16px; flex: 1; }
        .ts-card-rating { color: #CFA047; font-size: 12px; margin-bottom: 10px; display: flex; gap: 2px; }
        .ts-card-review { font-size: 13.5px; color: #475569; line-height: 1.6; font-style: italic; }
        .ts-card-footer { padding: 12px 16px; border-top: 1px solid #f1f5f9; display: flex; align-items: center; justify-content: space-between; background: #fafbfc; }
        .ts-badge { display: inline-flex; align-items: center; gap: 5px; padding: 4px 10px; border-radius: 20px; font-size: 11px; font-weight: 600; }
        .ts-badge.active { background: #fff; color: #061830; border: 1px solid #dde3ea; }
        .ts-badge.inactive { background: #f1f5f9; color: #64748b; border: 1px solid #eef1f5; }
        .ts-badge-dot { width: 6px; height: 6px; border-radius: 50%; }
        .active .ts-badge-dot { background: #CFA047; }
        .ts-actions { display: flex; gap: 6px; }
        .ts-act-btn { display: flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 8px; border: 1.5px solid #dde3ea; background: #fff; color: #6b7a90; cursor: pointer; font-size: 13px; transition: all 0.18s; }
        .ts-act-btn:hover { background: #f8fafc; border-color: #bcc7d4; color: #1a2332; }
        .ts-act-btn.edit:hover { border-color: #CFA047; color: #CFA047; background: #fffbf2; }
        .ts-act-btn.del:hover { border-color: #fca5a5; color: #ef4444; background: #fff1f2; }

        /* Empty state */
        .ts-empty { text-align: center; padding: 70px 24px; }
        .ts-empty-icon-wrap { width: 88px; height: 88px; border-radius: 24px; background: linear-gradient(135deg, #f8fafc, #fdf5e6); display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; box-shadow: 0 8px 24px rgba(207, 160, 71,0.15); }
        .ts-empty-icon-wrap i { font-size: 38px; color: #CFA047; }
        .ts-empty-title { font-size: 18px; font-weight: 700; color: #2d3a4b; margin-bottom: 8px; }
        .ts-empty-text { font-size: 14px; color: #9babbf; max-width: 300px; margin: 0 auto 24px; line-height: 1.6; }

        /* Skeleton Loading Cards */
        .ts-skel-card { border-radius: 14px; border: 1px solid #dde3ea; overflow: hidden; background: #fff; }
        .ts-skel-header { padding: 16px; display: flex; gap: 14px; border-bottom: 1px solid #f1f5f9; }
        .ts-skel-avatar { width: 48px; height: 48px; border-radius: 50%; background: linear-gradient(90deg, #f0f4f8 25%, #e4eaf1 50%, #f0f4f8 75%); background-size: 200% 100%; animation: ts-shimmer 1.4s infinite; }
        .ts-skel-lines { flex: 1; display: flex; flex-direction: column; gap: 8px; justify-content: center; }
        .ts-skel-line { height: 12px; border-radius: 6px; background: linear-gradient(90deg, #f0f4f8 25%, #e4eaf1 50%, #f0f4f8 75%); background-size: 200% 100%; animation: ts-shimmer 1.4s infinite; }
        .ts-skel-body { padding: 16px; display: flex; flex-direction: column; gap: 10px; }
        @keyframes ts-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

        /* Slide-in Drawer */
        .ts-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.5); backdrop-filter: blur(4px); z-index: 900; display: flex; justify-content: flex-end; }
        @keyframes ts-drop { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        .ts-drawer { width: 860px; max-width: 95vw; background: #fff; height: 100vh; display: flex; flex-direction: column; box-shadow: -8px 0 40px rgba(0,0,0,0.18); animation: ts-drop 0.28s cubic-bezier(0.22,1,0.36,1); }
        .ts-dh { padding: 22px 28px; border-bottom: 1px solid #eef1f5; display: flex; align-items: center; justify-content: space-between; background: linear-gradient(135deg, #061830, #040f1f); flex-shrink: 0; }
        .ts-dh-left { display: flex; align-items: center; gap: 14px; }
        .ts-dh-icon { width: 40px; height: 40px; border-radius: 12px; background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.25); display: flex; align-items: center; justify-content: center; color: #CFA047; font-size: 16px; }
        .ts-dh-title { font-size: 17px; font-weight: 700; color: #fff; margin: 0; }
        .ts-dh-sub { font-size: 12px; color: rgba(255,255,255,0.8); margin-top: 2px; }
        .ts-dh-actions button { background: rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.2); color: white; width: 34px; height: 34px; border-radius: 8px; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 14px; transition: background 0.18s; }
        .ts-dh-actions button:hover { background: rgba(255,255,255,0.22); }
        
        .ts-drawer-body { display: flex; flex: 1; overflow: hidden; }

        /* Drawer Image Panel */
        .ts-img-panel { width: 320px; background: #f8fafc; border-right: 1px solid #dde3ea; padding: 32px 28px; display: flex; flex-direction: column; overflow-y: auto; align-items: center; text-align: center; }
        .ts-img-banner { width: 160px; height: 160px; border-radius: 50%; overflow: hidden; background: #fff; border: 2px dashed #cbd5e1; display: flex; align-items: center; justify-content: center; position: relative; margin: 0 auto 24px; box-shadow: 0 4px 20px rgba(0,0,0,0.04); flex-shrink: 0; }
        .ts-img-banner img { width: 100%; height: 100%; object-fit: cover; }
        .ts-img-banner-placeholder { display: flex; flex-direction: column; align-items: center; gap: 8px; color: #94a3b8; }
        .ts-img-banner-placeholder i { font-size: 40px; color: #cbd5e1; }
        .ts-img-banner-placeholder span { font-size: 12px; font-weight: 500; }
        .ts-img-upload-btn { position: relative; overflow: hidden; display: flex; align-items: center; justify-content: center; gap: 8px; padding: 12px; background: white; border: 1.5px solid #dde3ea; border-radius: 10px; font-size: 13px; font-weight: 600; color: #475569; cursor: pointer; transition: all 0.2s; box-shadow: 0 2px 6px rgba(0,0,0,0.02); width: 100%; }
        .ts-img-upload-btn:hover { border-color: #CFA047; color: #CFA047; background: #fffbf2; }
        .ts-img-upload-btn input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
        
        .ts-img-tips { margin-top: 32px; background: white; border: 1px solid #dde3ea; border-radius: 12px; padding: 16px; text-align: left; width: 100%; box-sizing: border-box; }
        .ts-img-tips-title { font-size: 12px; font-weight: 700; color: #1e293b; margin-bottom: 10px; display: flex; align-items: center; gap: 6px; }
        .ts-img-tips-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px; }
        .ts-img-tips-list li { font-size: 11.5px; color: #64748b; display: flex; gap: 8px; line-height: 1.5; }
        .ts-img-tips-list li::before { content: '•'; color: #CFA047; font-weight: bold; }

        /* Drawer Form Panel */
        .ts-form-panel { flex: 1; padding: 32px 40px; overflow-y: auto; background: #fff; display: flex; flex-direction: column; gap: 24px; }

        .ts-section-title { font-size: 11px; font-weight: 700; color: #CFA047; text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 14px; display: flex; align-items: center; gap: 8px; }
        .ts-section-title::after { content: ''; flex: 1; height: 1px; background: #fdf5e6; }

        .ts-float-group { position: relative; margin-bottom: 6px; }
        .ts-float-inp { width: 100%; padding: 20px 16px 8px; border-radius: 10px; border: 1.5px solid #dde3ea; font-size: 14px; font-family: 'Inter', sans-serif; color: #2d3a4b; outline: none; background: #f9fbfc; transition: border-color 0.18s, box-shadow 0.18s; box-sizing: border-box; }
        .ts-float-inp:focus { border-color: #CFA047; box-shadow: 0 0 0 3px rgba(207, 160, 71,0.1); background: #fff; }
        .ts-float-inp:focus + .ts-float-label, .ts-float-inp:not(:placeholder-shown) + .ts-float-label { top: 7px; font-size: 10.5px; color: #CFA047; font-weight: 700; }
        .ts-float-label { position: absolute; top: 14px; left: 16px; font-size: 13px; color: #9babbf; font-weight: 500; pointer-events: none; transition: all 0.18s; }
        .ts-float-textarea { min-height: 100px; resize: vertical; line-height: 1.6; padding-top: 22px; }

        .ts-rating-selector { background: #f9fbfc; border: 1.5px solid #dde3ea; border-radius: 10px; padding: 14px 16px; margin-bottom: 6px; transition: border-color 0.18s; }
        .ts-rating-selector:focus-within, .ts-rating-selector:hover { border-color: #CFA047; box-shadow: 0 0 0 3px rgba(207, 160, 71,0.12); background: #fff; }
        .ts-rating-label { font-size: 11px; font-weight: 700; color: #CFA047; text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 12px; display: block; }
        .ts-stars-input { display: flex; gap: 8px; font-size: 24px; cursor: pointer; color: #e2e8f0; }
        .ts-stars-input i { transition: transform 0.2s, color 0.2s; }
        .ts-stars-input i:hover { transform: scale(1.15); }
        .ts-stars-input i.active { color: #CFA047; }
        .ts-stars-input i.active:hover { color: #b88d3b; }

        .ts-switch-row { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px; background: #f9fbfc; border: 1.5px solid #dde3ea; border-radius: 10px; }
        .ts-switch-label { font-size: 13px; font-weight: 600; color: #2d3a4b; }
        .ts-switch-sub { font-size: 11px; color: #9babbf; margin-top: 2px; }
        .ts-switch { position: relative; width: 40px; height: 24px; flex-shrink: 0; }
        .ts-switch input { opacity: 0; width: 0; height: 0; }
        .ts-slider { position: absolute; inset: 0; background: #dde3ea; border-radius: 50px; cursor: pointer; transition: 0.2s; }
        .ts-slider:before { content: ''; position: absolute; width: 18px; height: 18px; left: 3px; top: 3px; background: white; border-radius: 50%; transition: 0.2s; box-shadow: 0 1px 4px rgba(0,0,0,0.18); }
        .ts-switch input:checked + .ts-slider { background: #CFA047; }
        .ts-switch input:checked + .ts-slider:before { transform: translateX(16px); }

        .ts-df { padding: 18px 28px; border-top: 1px solid #eef1f5; display: flex; justify-content: space-between; align-items: center; background: #f9fbfc; flex-shrink: 0; }
        .ts-df-hint { font-size: 12px; color: #9babbf; }
        .ts-df-btns { display: flex; gap: 12px; }
        .ts-btn-cancel { padding: 10px 22px; border-radius: 9px; font-weight: 600; cursor: pointer; background: white; border: 1.5px solid #dde3ea; color: #4a5568; font-family: 'Inter', sans-serif; font-size: 13px; transition: all 0.18s; }
        .ts-btn-cancel:hover { background: #f7f9fc; }
        .ts-btn-save { padding: 10px 26px; border-radius: 9px; font-weight: 600; cursor: pointer; background: linear-gradient(135deg, #CFA047 0%, #061830 100%); border: none; color: white; font-family: 'Inter', sans-serif; font-size: 13px; box-shadow: 0 2px 8px rgba(207, 160, 71,0.3); transition: all 0.18s; display: flex; align-items: center; gap: 8px; }
        .ts-btn-save:hover { box-shadow: 0 4px 14px rgba(207, 160, 71,0.4); transform: translateY(-1px); }

        .ts-del-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.6); backdrop-filter: blur(4px); display: flex; justify-content: center; align-items: center; z-index: 999; }
        @keyframes ts-slide-up { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>

      <div className="ts-wrap">
        {/* Hero Banner */}
        <div className="ts-hero">
          <div className="ts-hero-left">
            <div className="ts-hero-eyebrow"><i className="fa-solid fa-comments"></i> Testimonials</div>
            <h2 className="ts-hero-title">Client Feedback</h2>
            <p className="ts-hero-sub">Manage client reviews to build trust on your landing page.</p>
          </div>
          <div className="ts-hero-right">
            {!loading && (
              <>
                <div className="ts-hero-stat">
                  <div className="ts-hero-stat-val">{testimonials.length}</div>
                  <div className="ts-hero-stat-label">Total</div>
                </div>
                <div className="ts-hero-stat">
                  <div className="ts-hero-stat-val">{testimonials.filter(t => t.is_active).length}</div>
                  <div className="ts-hero-stat-label">Active</div>
                </div>
              </>
            )}
            <button className="ts-create-btn" onClick={handleOpenCreate}>
              <i className="fa-solid fa-plus"></i> Add Testimonial
            </button>
          </div>
        </div>

        {error && <div className="ts-error"><i className="fa-solid fa-circle-exclamation"></i>{error}</div>}

        <div className="ts-section-header">
          <div className="ts-section-label">
            <i className="fa-solid fa-grip" style={{ color: '#CFA047' }}></i>
            All Testimonials
            <span className="ts-count-badge">{filteredTestimonials.length}</span>
          </div>
          <div className="ts-search-box">
            <i className="fa-solid fa-magnifying-glass"></i>
            <input 
              type="text" 
              placeholder="Search by name, role, or review..." 
              value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)} 
            />
          </div>
        </div>

        {loading ? (
          <div className="ts-grid-list">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="ts-skel-card">
                <div className="ts-skel-header">
                  <div className="ts-skel-avatar"></div>
                  <div className="ts-skel-lines">
                    <div className="ts-skel-line" style={{ width: '60%' }}></div>
                    <div className="ts-skel-line" style={{ width: '40%' }}></div>
                  </div>
                </div>
                <div className="ts-skel-body">
                  <div className="ts-skel-line" style={{ width: '90%' }}></div>
                  <div className="ts-skel-line" style={{ width: '95%' }}></div>
                  <div className="ts-skel-line" style={{ width: '70%' }}></div>
                </div>
              </div>
            ))}
          </div>
        ) : testimonials.length === 0 ? (
          <div className="ts-empty">
            <div className="ts-empty-icon-wrap"><i className="fa-regular fa-comment-dots"></i></div>
            <div className="ts-empty-title">No testimonials yet</div>
            <div className="ts-empty-text">Start adding client feedback to showcase social proof and build trust with your audience.</div>
            <button className="ts-create-btn" onClick={handleOpenCreate} style={{ margin: '0 auto', display: 'inline-flex', background: 'linear-gradient(135deg, #CFA047, #061830)', color: 'white' }}>
              <i className="fa-solid fa-plus"></i> Add Your First Testimonial
            </button>
          </div>
        ) : filteredTestimonials.length === 0 ? (
          <div className="ts-empty" style={{ padding: '50px 24px' }}>
            <div className="ts-empty-icon-wrap" style={{ width: 72, height: 72, margin: '0 auto 16px' }}>
              <i className="fa-solid fa-magnifying-glass" style={{ fontSize: 28 }}></i>
            </div>
            <div className="ts-empty-title">No matching testimonials</div>
            <div className="ts-empty-text" style={{ maxWidth: 350 }}>We couldn't find any testimonials matching "<strong>{searchTerm}</strong>".</div>
            <button onClick={() => setSearchTerm('')} style={{ background: '#fff', border: '1px solid #dde3ea', padding: '10px 20px', borderRadius: '10px', cursor: 'pointer', fontWeight: 600, color: '#475569', marginTop: '8px', display: 'inline-block' }}>Clear Search</button>
          </div>
        ) : (
          <div className="ts-grid-list">
            {filteredTestimonials.map(item => (
              <div key={item.id} className="ts-test-card">
                <div className="ts-card-header">
                  {item.avatar ? (
                    <img src={item.avatar.startsWith('http') ? item.avatar : `${API_URL}${item.avatar.startsWith('/') ? '' : '/'}${item.avatar}`} alt={item.name} className="ts-card-avatar" />
                  ) : (
                    <div className="ts-card-avatar-placeholder"><i className="fa-solid fa-user"></i></div>
                  )}
                  <div className="ts-card-client-info">
                    <div className="ts-card-name">{item.name}</div>
                    <div className="ts-card-role">{item.role}</div>
                  </div>
                </div>
                <div className="ts-card-body">
                  <div className="ts-card-rating">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className={i < item.rating ? "fa-solid fa-star" : "fa-regular fa-star"} style={{ color: i < item.rating ? '#CFA047' : '#e2e8f0' }}></i>
                    ))}
                  </div>
                  <div className="ts-card-review">"{item.review.length > 120 ? item.review.substring(0, 120) + '...' : item.review}"</div>
                </div>
                <div className="ts-card-footer">
                  <span className={`ts-badge ${item.is_active ? 'active' : 'inactive'}`}>
                    <span className="ts-badge-dot"></span>
                    {item.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <div className="ts-actions">
                    <button className="ts-act-btn edit" onClick={() => handleOpenEdit(item)} title="Edit">
                      <i className="fa-solid fa-pen-to-square"></i>
                    </button>
                    <button className="ts-act-btn del" onClick={() => setItemToDelete(item)} title="Delete">
                      <i className="fa-solid fa-trash-can"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Slide-in Drawer */}
      {showFormModal && (
        <div className="ts-overlay" onClick={() => setShowFormModal(false)}>
          <div className="ts-drawer" onClick={e => e.stopPropagation()}>
            <div className="ts-dh">
              <div className="ts-dh-left">
                <div className="ts-dh-icon"><i className="fa-solid fa-comments"></i></div>
                <div>
                  <div className="ts-dh-title">{editingItem ? 'Edit Testimonial' : 'Add New Testimonial'}</div>
                  <div className="ts-dh-sub">{editingItem ? 'Update the client feedback details' : 'Add a new client review'}</div>
                </div>
              </div>
              <div className="ts-dh-actions">
                <button onClick={() => setShowFormModal(false)}><i className="fa-solid fa-xmark"></i></button>
              </div>
            </div>

            <form onSubmit={handleFormSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div className="ts-drawer-body">
                
                {/* Left: Avatar Upload Panel */}
                <div className="ts-img-panel">
                  <div className="ts-img-banner">
                    {imagePreview ? (
                      <img src={imagePreview} alt="Avatar Preview" />
                    ) : (
                      <div className="ts-img-banner-placeholder">
                        <i className="fa-solid fa-circle-user"></i>
                        <span>No Avatar</span>
                      </div>
                    )}
                  </div>
                  <label className="ts-img-upload-btn">
                    <i className="fa-solid fa-cloud-arrow-up"></i> Upload Avatar
                    <input type="file" accept="image/*" onChange={handleImageChange} />
                  </label>
                  <div className="ts-img-tips">
                    <div className="ts-img-tips-title"><i className="fa-solid fa-circle-info" style={{ color: '#CFA047' }}></i> Avatar Guidelines</div>
                    <ul className="ts-img-tips-list">
                      <li>Square images work best (1:1 ratio)</li>
                      <li>Recommended size: 200x200px</li>
                      <li>Max file size: 2MB</li>
                      <li>Supported: JPG, PNG, WebP</li>
                    </ul>
                  </div>
                </div>

                {/* Right: Form Panel */}
                <div className="ts-form-panel">
                  <div>
                    <div className="ts-section-title"><i className="fa-solid fa-user"></i> Client Info</div>
                    <div className="ts-float-group">
                      <input className="ts-float-inp" type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder=" " required />
                      <label className="ts-float-label">Client Name *</label>
                    </div>
                    <div className="ts-float-group" style={{ marginTop: '12px' }}>
                      <input className="ts-float-inp" type="text" name="role" value={formData.role} onChange={handleInputChange} placeholder=" " required />
                      <label className="ts-float-label">Designation / Role *</label>
                    </div>
                  </div>

                  <div>
                    <div className="ts-section-title"><i className="fa-solid fa-star"></i> Review Details</div>
                    <div className="ts-rating-selector">
                      <label className="ts-rating-label">Rating</label>
                      <div className="ts-stars-input">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <i 
                            key={star} 
                            className={`fa-solid fa-star ${star <= formData.rating ? 'active' : ''}`}
                            onClick={() => handleInputChange({ target: { name: 'rating', value: star } })}
                          ></i>
                        ))}
                      </div>
                    </div>
                    <div className="ts-float-group" style={{ marginTop: '12px' }}>
                      <textarea className="ts-float-inp ts-float-textarea" name="review" value={formData.review} onChange={handleInputChange} placeholder=" " required></textarea>
                      <label className="ts-float-label">Review Text *</label>
                    </div>
                  </div>

                  <div>
                    <div className="ts-section-title"><i className="fa-solid fa-gear"></i> Settings</div>
                    <div className="ts-switch-row">
                      <div>
                        <div className="ts-switch-label">Active</div>
                        <div className="ts-switch-sub">Show this testimonial on the landing page</div>
                      </div>
                      <label className="ts-switch">
                        <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleInputChange} />
                        <span className="ts-slider"></span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="ts-df">
                <span className="ts-df-hint"><i className="fa-solid fa-asterisk" style={{ fontSize: '9px', verticalAlign: 'middle', color: '#CFA047' }}></i> Required fields</span>
                <div className="ts-df-btns">
                  <button type="button" className="ts-btn-cancel" onClick={() => setShowFormModal(false)}>Cancel</button>
                  <button type="submit" className="ts-btn-save"><i className="fa-solid fa-floppy-disk"></i> {editingItem ? 'Update Testimonial' : 'Save Testimonial'}</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {itemToDelete && (
        <div className="ts-del-overlay" onClick={() => setItemToDelete(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', padding: '28px 24px', borderRadius: '16px', width: '320px', textAlign: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', animation: 'ts-slide-up 0.2s ease-out' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#fee2e2', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', margin: '0 auto 16px' }}>
              <i className="fa-solid fa-triangle-exclamation"></i>
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', margin: '0 0 8px' }}>Delete Testimonial</h3>
            <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 24px', lineHeight: 1.5 }}>
              Are you sure you want to delete the testimonial from <strong>"{itemToDelete.name}"</strong>? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button style={{ flex: 1, padding: '12px', borderRadius: '10px', fontWeight: 600, cursor: 'pointer', border: 'none', background: '#f1f5f9', color: '#475569' }} onClick={() => setItemToDelete(null)}>Cancel</button>
              <button style={{ flex: 1, padding: '12px', borderRadius: '10px', fontWeight: 600, cursor: 'pointer', border: 'none', background: '#ef4444', color: 'white' }} onClick={executeDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminTestimonials;
